/**
 * testiStrumenti — come la console scrive prezzi, quote e stati degli strumenti a canone (atlante cap. 251, tappa 4).
 * Un posto solo per le parole e i formati, così la linguetta dei prezzi, la pagina «Consumi» e la scheda azienda
 * dicono le stesse cose nello stesso modo.
 */

import { soloAConsumo, type StrumentoACanone, type UnitaDiConsumo, type ComportamentoAlTetto } from '@shared/strumenti/listino';
import type { RigaDiConsumo } from '@shared/strumenti/consumoDellAzienda';
import type { TettoDelloStrumento } from '@shared/strumenti/tetto';
import type { SoftChipTone } from '../SoftChip';

const EURO = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 2 });
const EURO_FINE = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 4 });
const EURO_CENT = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const DATA = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });

/** «69 €», «30,50 €». */
export const euro = (n: number): string => (Number.isInteger(n) ? EURO : EURO_CENT).format(n);
/** Il prezzo di un'unità: «0,40 €», «0,155 €». */
export const euroUnita = (n: number): string => EURO_FINE.format(n);
/** L'extra maturato, che è sempre a centesimi interi: «12,40 €». */
export const eurDaCentesimi = (c: number): string => EURO_CENT.format(c / 100);
export const data = (ms: number): string => DATA.format(new Date(ms));
/** L'ultimo giorno di qualcosa che finisce a un istante: una prova che scade alla mezzanotte del 1° novembre dura «fino al 31 ott». */
export const ultimoGiorno = (fineMs: number): string => data(fineMs - 1);

const UNITA: Record<UnitaDiConsumo, { una: string; tante: string; al: string }> = {
  minuti: { una: 'minuto', tante: 'minuti', al: 'al minuto' },
  messaggi: { una: 'messaggio', tante: 'messaggi', al: 'a messaggio' },
  // I calcoli del giro del tecnico (Manutenzioni, 18/10/2026).
  giri: { una: 'giro', tante: 'giri', al: 'a giro' },
  // I servizi solo a consumo contano l'importo, già al prezzo del cliente (atlante cap. 251, tappa 5).
  centesimi: { una: 'centesimo', tante: 'centesimi', al: 'al centesimo' },
  // I progettisti sbloccati nei Permessi edilizi (PERMESSI-EDILIZI-MODEL §12.1).
  sblocchi: { una: 'sblocco', tante: 'sblocchi', al: 'a sblocco' },
};
export const unita = (u: UnitaDiConsumo, n: number): string => (n === 1 ? UNITA[u].una : UNITA[u].tante);
/** «al minuto», «a messaggio», «al centesimo». */
export const alUnita = (u: UnitaDiConsumo): string => UNITA[u].al;

/** «60 minuti compresi, poi 0,40 € al minuto»; per i servizi solo a consumo «si paga quello che si usa». */
export function fraseConsumo(s: Pick<StrumentoACanone, 'consumo' | 'canone_mensile_eur'>): string {
  if (s.consumo === null) return 'tutto compreso, niente consumi da pagare a parte';
  if (soloAConsumo(s)) return 'si paga quello che si usa (il costo del fornitore per il ricarico), in fattura a fine mese';
  const { unita: u, quota_compresa: q, prezzo_extra_eur: p } = s.consumo;
  return `${q} ${unita(u, q)} compresi, poi ${euroUnita(p)} ${alUnita(u)}`;
}

/** Le condizioni in una riga: «99 € al mese, 60 minuti compresi, poi 0,40 € al minuto», o «solo a consumo: …». */
export function condizioniDette(s: Pick<StrumentoACanone, 'consumo' | 'canone_mensile_eur'>): string {
  return soloAConsumo(s) ? `solo a consumo: ${fraseConsumo(s)}` : `${euro(s.canone_mensile_eur)} al mese, ${fraseConsumo(s)}`;
}

export const AL_TETTO: Record<ComportamentoAlTetto, string> = {
  ferma_iniziativa: 'al tetto smette di scrivere per primo, ma risponde',
  solo_avviso: 'al tetto continua, e si avvisa soltanto',
  ferma_tutto: 'al tetto non parte più niente di nuovo, nemmeno quello automatico',
};

/** Come paga lo strumento un'azienda, in poche parole, e il colore del gettone. */
export function comePaga(r: Pick<RigaDiConsumo, 'via' | 'regalato'>): { testo: string; tono: SoftChipTone } {
  if (r.regalato) return { testo: 'regalo', tono: 'primary' };
  const v = r.via;
  switch (v.via) {
    case 'senza_addebito': return { testo: 'gratuita per decisione', tono: 'default' };
    case 'nell_abbonamento':
      return v.gratis_fino_al_ms !== null
        ? { testo: `nell’abbonamento, gratis fino al ${ultimoGiorno(v.gratis_fino_al_ms)}`, tono: 'info' }
        : { testo: 'paga', tono: 'success' };
    case 'gratis_fino_a_fine_prova': return { testo: `prova senza carta fino al ${ultimoGiorno(v.fino_al_ms)}`, tono: 'info' };
    case 'pagamento_in_sospeso': return { testo: 'pagamento in sospeso', tono: 'error' };
    case 'abbonamento_in_pausa': return { testo: 'abbonamento in pausa', tono: 'warning' };
    case 'serve_il_pagamento': return { testo: 'acceso senza pagamento', tono: 'error' };
  }
}

/** Il modo di pagare in una parola, per contare le aziende: senza date, che dividerebbero i gruppi. */
export function categoriaDiPagamento(r: Pick<RigaDiConsumo, 'via' | 'regalato'>): string {
  if (r.regalato) return 'regalo';
  switch (r.via.via) {
    case 'senza_addebito': return 'gratuite per decisione';
    case 'nell_abbonamento': return r.via.gratis_fino_al_ms !== null ? 'in prova con la carta' : 'pagano';
    case 'gratis_fino_a_fine_prova': return 'in prova senza carta';
    case 'serve_il_pagamento': return 'accese senza pagamento';
    default: return 'pagamento in sospeso';
  }
}

/** «69 € (pari al canone)», «20 € (di partenza)», «30 €», «nessun tetto». */
export function testoTetto(t: TettoDelloStrumento, s: Pick<StrumentoACanone, 'tetto_predefinito_eur'>): string {
  if (t.tipo === 'nessuno') return 'nessun tetto';
  if (t.tipo === 'scelto') return euro(t.eur);
  return `${euro(t.eur)} (${s.tetto_predefinito_eur === null ? 'pari al canone' : 'di partenza'})`;
}
